import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { forwardRef } from 'react';
import { cn } from './utils/shadcn-utils';
const buttonVariants = cva("font-semibold inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-all disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[var(--focus-ring-width)] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive", {
    variants: {
        variant: {
            default: 'bg-[var(--button-bg-default)] text-primary-foreground shadow-xs hover:bg-[var(--button-bg-hover-default)]',
            destructive: 'bg-[var(--button-bg-destructive)] text-white shadow-xs hover:bg-[var(--button-bg-hover-destructive)] focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
            outline: 'border border-input-border bg-[var(--button-bg-outline)] shadow-xs hover:bg-[var(--button-bg-hover-outline)] hover:text-accent-foreground dark:bg-input/30 dark:border-input-border dark:hover:bg-input/50',
            secondary: 'bg-[var(--button-bg-secondary)] text-secondary-foreground shadow-xs hover:bg-[var(--button-bg-hover-secondary)] transition-colors',
            ghost: 'hover:bg-[var(--button-bg-hover-ghost)] hover:text-accent-foreground dark:hover:bg-accent/50',
            link: 'text-primary underline-offset-4 hover:underline'
        },
        size: {
            default: 'h-[var(--button-height-default)] px-[var(--button-padding-x-default)] py-[var(--button-padding-y-default)] has-[>svg]:px-[var(--button-padding-x-with-icon-default)]',
            sm: 'h-[var(--button-height-sm)] rounded-md gap-[var(--button-gap-sm)] px-[var(--button-padding-x-sm)] has-[>svg]:px-[var(--button-padding-x-with-icon-sm)]',
            xs: 'h-[var(--button-height-xs)] rounded-md gap-[var(--button-gap-xs)] px-[var(--button-padding-x-xs)] has-[>svg]:px-[var(--button-padding-x-with-icon-xs)]',
            lg: 'h-[var(--button-height-lg)] rounded-md px-[var(--button-padding-x-lg)] has-[>svg]:px-[var(--button-padding-x-with-icon-lg)]',
            xl: 'h-[var(--button-height-xl)] rounded-md px-[var(--button-padding-x-xl)] has-[>svg]:px-[var(--button-padding-x-with-icon-xl)]',
            icon: 'size-[var(--button-size-icon)]'
        },
        disabled: {
            true: 'cursor-not-allowed opacity-50'
        }
    },
    compoundVariants: [
        {
            variant: 'default',
            disabled: true,
            class: 'opacity-50'
        },
        {
            variant: 'secondary',
            disabled: true,
            class: 'text-muted-foreground/60'
        }
    ],
    defaultVariants: {
        variant: 'default',
        size: 'default'
    }
});
const Button = forwardRef(({ className, variant, size, disabled, asChild = false, loading = false, children, rippleAnimationTimeMs = 640, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const handlePointerDown = (event) => {
        if (disabled ?? false) {
            return;
        }
        if (loading ?? false) {
            return;
        }
        const targetElement = event.currentTarget;
        const rect = targetElement.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const maxWidth = Math.max(x, targetElement.offsetWidth - x);
        const maxHeight = Math.max(y, targetElement.offsetHeight - y);
        const radius = Math.sqrt(maxWidth * maxWidth + maxHeight * maxHeight);
        const overlayElement = document.createElement('div');
        Object.assign(overlayElement.style, {
            position: 'absolute',
            top: '0',
            right: '0',
            bottom: '0',
            left: '0',
            overflow: 'hidden',
            borderRadius: 'inherit',
            transform: 'perspective(0)'
        });
        targetElement.appendChild(overlayElement);
        const rippleElement = document.createElement('div');
        Object.assign(rippleElement.style, {
            position: 'absolute',
            top: `${y - radius}px`,
            left: `${x - radius}px`,
            height: `${radius * 2}px`,
            width: `${radius * 2}px`,
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            transform: 'scale(0)',
            transition: `opacity ${rippleAnimationTimeMs}ms, transform ${rippleAnimationTimeMs}ms`
        });
        overlayElement.appendChild(rippleElement);
        window.setTimeout(function () {
            rippleElement.style.transform = 'scale(1)';
        }, 0);
        window.setTimeout(function () {
            rippleElement.remove();
            overlayElement.remove();
        }, rippleAnimationTimeMs);
    };
    if (asChild) {
        return (_jsx(Comp, { ref: ref, "data-slot": "button", className: cn(buttonVariants({ variant, size, disabled, className }), 'relative'), ...props, disabled: disabled, onPointerDownCapture: handlePointerDown, children: children }));
    }
    return (_jsxs(Comp, { ref: ref, "data-slot": "button", className: cn(buttonVariants({ variant, size, disabled, className }), 'relative overflow-hidden'), ...props, onPointerDownCapture: handlePointerDown, disabled: disabled || loading, children: [loading ? _jsx("div", { className: "opacity-0", children: children }) : children, _jsx(AnimatePresence, { children: loading && (_jsx(motion.div, { initial: { opacity: 0, scale: 0.8 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 0.8 }, transition: { duration: 0.2 }, className: "absolute inset-0 flex items-center justify-center", children: _jsx(Loader2, { className: "h-4 w-4 animate-spin" }) }, "loading")) })] }));
});
export { Button, buttonVariants };
//# sourceMappingURL=button.js.map